import gatewayService from '@/modules/common/services/gatewayService';
import httpService from '@/modules/common/services/httpService';

/**
 * Base URL for the same API as the Vue client portal editor (Edit portal actions).
 * Vue uses VUE_APP_API_URL when new_api: true, which points to core; POV default is frontage /api2/.
 * @returns {string | null} Base URL (with trailing slash) or null to use default httpService
 */
function getCoreApiBaseUrl() {
  if (typeof window === 'undefined') return null;
  const pageHost = (window.location.hostname || '').toLowerCase();

  const v = window.vue_config?.VUE_APP_API_URL;
  if (v) {
    return v.endsWith('/') ? v : `${v}/`;
  }
  const p = window.pov_config?.VUE_APP_API_URL;
  if (p && p !== '/api2/') {
    return p.endsWith('/') ? p : `${p}/`;
  }
  if (pageHost.includes('frontage-') && (pageHost.includes('external.') || pageHost.includes('int-eks'))) {
    const coreHost = pageHost.replace(/^frontage-/, 'core-');
    return `${window.location.protocol}//${coreHost}/`;
  }
  return null;
}

const BASE = 'v3/web_ai_widget/widget_configurations';
const CONVERSATIONS_BASE = 'v3/clientportal/chat/conversations';

/**
 * Normalize API response to UI shape: uid → id, business_uid → business_id; build synthetic business.
 * @param {object} data - Raw widget_configuration from API (uid, business_uid, widget_title, + config fields)
 * @returns {{ config: object, business: { id: string, name: string } }}
 */
function normalizeResponse(data) {
  if (!data) {
    return { config: null, business: { id: '', name: '' } };
  }
  // Destructure top-level fields so they are not duplicated; widget_title is the Widget Title (header).
  const { uid, business_uid: bizUid, widget_title: widgetTitle, ...rest } = data;
  const config = {
    id: uid,
    business_id: bizUid,
    widget_title: widgetTitle,
    ...rest,
  };
  const business = {
    id: bizUid ?? '',
    name: widgetTitle ?? '',
  };
  return { config, business };
}

/**
 * Get main widget configuration for a business. Returns null config when none exists (404).
 * @param {string} businessUid - Business UID (from AuthStore)
 * @returns {Promise<{ config: object | null, business: { id: string, name: string } }>}
 */
async function getMainConfig(businessUid) {
  try {
    const res = await gatewayService.get(`${BASE}/main`, {
      business_uid: businessUid, // API expects snake_case
    });
    const raw = res?.data?.data?.widget_configuration ?? res?.data?.widget_configuration;
    return normalizeResponse(raw);
  } catch (err) {
    if (err?.response?.status === 404) {
      return {
        config: null,
        business: { id: businessUid, name: '' },
      };
    }
    throw err;
  }
}

/**
 * Upsert main widget configuration for a business.
 * @param {string} businessUid - Business UID (from AuthStore)
 * @param {object} payload - Config fields only (no id, uid, business_id); snake_case
 * @returns {Promise<{ config: object, business: { id: string, name: string } }>}
 */
async function upsertMainConfig(businessUid, payload) {
  const body = {
    business_uid: businessUid, // API expects snake_case
    ...payload,
  };
  const res = await gatewayService.put(`${BASE}/main`, body);
  const raw = res?.data?.data?.widget_configuration ?? res?.data?.widget_configuration;
  return normalizeResponse(raw);
}

function parseActionsResponse(res) {
  const body = res?.data ?? {};
  let actions = [];
  if (Array.isArray(body.data)) {
    actions = body.data;
  } else if (Array.isArray(body.data?.data)) {
    actions = body.data.data;
  } else if (Array.isArray(body.data?.actions)) {
    actions = body.data.actions;
  } else if (Array.isArray(body.actions)) {
    actions = body.actions;
  } else if (Array.isArray(body)) {
    actions = body;
  }
  return actions.map((a) => {
    const key = String(a.name ?? a.key ?? a.type ?? '').toLowerCase() || 'action';
    const defaultLabel = a.title ?? a.label ?? a.rich_title ?? a.display_name ?? a.default_title ?? key;
    return { key, defaultLabel };
  });
}

/**
 * Get the list of client portal actions for this business (same as Edit portal actions dialog).
 * Tries core API first; on failure or empty, falls back to same-origin (api2) so labels always load.
 * @param {string} businessUid - Business UID (from AuthStore)
 * @returns {Promise<Array<{ key: string, defaultLabel: string }>>}
 */
async function getAvailablePortalActions(businessUid) {
  if (!businessUid) return [];
  const ACTIONS_PATH = 'business/web_presence/v2/actions/';
  const params = { context: 'client_portal', new_api: true, teams_view_filter: '{}' };
  const coreBase = getCoreApiBaseUrl();
  const client = coreBase ? httpService.create(coreBase, true) : null;
  if (client) {
    try {
      const res = await client.get(ACTIONS_PATH, params);
      const list = parseActionsResponse(res);
      if (list.length > 0) return list;
    } catch (_) {
      // Fall through to same-origin
    }
  }
  try {
    const res = await httpService.get(ACTIONS_PATH, params);
    return parseActionsResponse(res);
  } catch (_) {
    return [];
  }
}

/**
 * List conversations for the logged-in business (backend derives business from auth; do not pass business_uid).
 * @param {object} params - { page?: number, per_page?: number, query?: string }
 * @returns {Promise<{ conversations: Array<object>, total: number }>}
 */
async function getConversations(params = {}) {
  const {
    page = 1,
    per_page: perPage = 25,
    query,
    sort = 'started_at:desc',
  } = params;
  const requestParams = { page, per_page: perPage, sort };
  if (query != null && String(query).trim() !== '') {
    requestParams.q = String(query).trim();
  }
  const res = await gatewayService.get(CONVERSATIONS_BASE, requestParams);
  const data = res?.data?.data ?? res?.data;
  const conversations = Array.isArray(data?.conversations) ? data.conversations : [];
  const total = typeof data?.total === 'number' ? data.total : 0;
  return { conversations, total };
}

/**
 * Get a single conversation by uid (for transcript modal). Backend scopes to logged-in business.
 * @param {string} uid - Conversation UID
 * @returns {Promise<object>} Conversation object (uid, transcript, summary, etc.)
 */
async function getConversation(uid) {
  const res = await gatewayService.get(`${CONVERSATIONS_BASE}/${uid}`);
  const conversation = res?.data?.data ?? res?.data;
  return conversation ?? null;
}

/**
 * GET v2/weekly_availabilities/:uid (general availability) via APIGW. Used for default business hours.
 * @param {string} weeklyAvailabilityUid - weekly_availability_uid from owner staff
 * @returns {Promise<{ time_slots_by_day: Array }>} Response with time_slots_by_day
 */
async function getWeeklyAvailabilityGeneral(weeklyAvailabilityUid) {
  if (!weeklyAvailabilityUid || !weeklyAvailabilityUid.trim()) {
    return null;
  }
  const res = await httpService.get(`/v2/weekly_availabilities/${weeklyAvailabilityUid.trim()}`, {
    general_availability: 'true',
    use_selected_staff_time_zone: 'true',
  });
  const data = res?.data ?? res;
  return data ?? null;
}

export default {
  getMainConfig,
  upsertMainConfig,
  getAvailablePortalActions,
  getConversations,
  getConversation,
  getWeeklyAvailabilityGeneral,
};
